import { FormComponent, type FormConfiguration, type QueryParams } from "../../../components/Form/types/types";
import { getAssignedDataGroup } from "../../../core/router/auth";
import type { Entity, ID } from "../../../core/types/fe/Entity";

export const generateDefaultFormEntity = (formConfig: FormConfiguration): Entity => {
    if (!formConfig) {
        return {};
    }

    const getPropDefaultValue = (type: string, defaultValue: string | boolean | number | object | object[] | undefined) => {
        if (defaultValue) {
            return defaultValue;
        }
        switch (type) {
            case FormComponent.MULTI_SELECT_DYNAMIC:
            case FormComponent.ACCEPT_TYPE_SELECTOR:
            case FormComponent.DATA_GROUP_USERS_TABLE:
            case FormComponent.DYNAMIC_PROPERTY_GROUP_PROPERTIES_TABLE:
                return [];
            case FormComponent.CHECK_BOX:
            case FormComponent.READ_ONLY_CHECK:
                return false;
            default:
                return undefined;
        }
    };

    const dataProps = formConfig.tabs.flatMap((tab) =>
        tab.fields.map((field) => {
            return { name: field.name, type: field.component, defaultValue: field.attributes?.defaultValue };
        }),
    );

    const generatedFormEntity = {} as Entity;

    dataProps.forEach((prop) => {
        if (Object.hasOwn(generatedFormEntity, prop.name)) {
            // prop is already set.
            // This can happen when a prop has different components or configurations assigned.
            return;
        }

        Object.defineProperty(generatedFormEntity, prop.name, {
            value: getPropDefaultValue(prop.type, prop.defaultValue),
            writable: false,
            enumerable: true,
        });
    });

    return generatedFormEntity;
};

export const createRequestParams = (formConfig: FormConfiguration, id?: ID, resourceUrl?: string, resourceName?: string) => {
    return {
        resourceName,
        resource: resourceUrl || formConfig.resource,
        dataGroupId: formConfig.useDataGroup ? getAssignedDataGroup()?.code : undefined,
        entityId: id,
    } as QueryParams;
};

export const isFieldSetByUriParam = (name: string, uriParams: URLSearchParams) => !!uriParams.get(name);

export const setFieldsValueByParams = (formValues: Entity, uriParams: URLSearchParams) => {
    const updatedFormValues: Entity = { ...formValues };
    Object.getOwnPropertyNames(formValues).forEach((propName) => {
        if (isFieldSetByUriParam(propName, uriParams)) {
            (updatedFormValues[propName as keyof object] as string) = uriParams.get(propName)!;
        }
    });

    return updatedFormValues;
};

export const setFormConfigByParams = (data: FormConfiguration, uriParams: URLSearchParams) => {
    return {
        ...data,
        tabs: data.tabs.map((tab) => {
            return {
                ...tab,
                fields: tab.fields.map((field) => {
                    return {
                        ...field,
                        attributes: {
                            ...field.attributes,
                            readOnly: isFieldSetByUriParam(field.name, uriParams) || field.attributes?.readOnly,
                        },
                    };
                }),
            };
        }),
    };
};
